import clsx from "clsx";
import { default as Image } from "next/image";
import Link from "next/link";
import { Suspense } from "react";

import AspectRatioProvider from "@/components/modifiers/AspectRatioProvider";

import { ProductCard as PC } from "@/lib/centra/formatters/formatProductCards/formatProductCard";
import { Price, Subtitle } from "..";
import DepictWrapper from "./DepictWrapper";
import PCToolTip from "./PCToolTip";
import { ProductLabelTags } from "./ProductLabelTags";
import styles from "./product-card.module.css";
import ProductCardActionNoSSr from "./ProductCardActionNoSSr";
import { ProductCardCarousel } from "./ProductCardCarousel";
import { ProductSaleTag } from "./ProductSaleTag";

const ProductCard = ({
  product,
  cardsPerRow,
  inWishlist,
  className,
  actionClassName,
  preselectSizeId,
}: {
  product: PC;
  cardsPerRow?: number;
  inWishlist?: boolean;
  className?: string;
  actionClassName?: string;
  preselectSizeId?: string;
}) => {
  const images = [product?.gallery?.image, product?.gallery?.secondImage].filter(
    (img): img is { href: string; alt: string } => img !== undefined,
  );
  const allImages = product?.gallery?.allImages ?? images;
  const slug = product?.core?.slug ?? "#";
  const name = product?.core?.name;
  const sizes = product?.description?.size;
  const swatch = product?.description?.swatch;
  const discountPercent = product?.description?.price?.[0]?.discountPercent;
  const isSoldOut =
    sizes && sizes.length > 0
      ? sizes.every((size) => size.stock === false)
      : product.soldOut;

  if (product?.id === "1544" || product?.id === "1545") {
    return null;
  }

  return (
    <DepictWrapper
      asChild
      depictListingResultId={product.depictListingResultId}
      depictSearchResultId={product.depictSearchResultId}
    >
      <div
        className={clsx(styles.card__container, className)}
        style={
          cardsPerRow
            ? {
                width: `calc(${cardsPerRow > 1 ? 100 / cardsPerRow : 100}% - ${
                  cardsPerRow > 1 ? 1 : 0
                }rem)`,
                minWidth: cardsPerRow > 1 ? "200px" : "50%",
                maxWidth: `calc(100% - 23.5vw)`,
                justifySelf: "center",
                margin: cardsPerRow > 1 ? "0" : "0 auto",
              }
            : {}
        }
      >
        <Link
          prefetch={true}
          href={`/product/${slug}`}
          className={`product-card ${styles["product-card"]}`}
        >
          <AspectRatioProvider height={3} width={2}>
            <ProductCardCarousel images={allImages} productName={name ?? ""} />

            <ProductCardActionNoSSr
              sizes={sizes ?? []}
              product={product}
              actionClassName={actionClassName}
              preselectSizeId={preselectSizeId}
            />
          </AspectRatioProvider>

          <div className={styles["product-description"]}>
            {/* TODO:add toolTip here if name is too long */}
            <div
              className={clsx(styles["product-name"], "uppercase", "truncate")}
            >
              {name}
            </div>
            {!inWishlist && (
              <PCToolTip product={product}>
                <div className={styles.swatches}>
                  <>
                    <div
                      className={styles.swatch}
                      style={{ background: `#${swatch?.code}` }}
                    />
                    {product?.description?.variants &&
                      product?.description?.variants.length > 1 && (
                        <>
                          <p className={styles.plus}>+</p>
                          <p className={styles["more-swatches"]}>
                            {product?.description?.variants.length - 1}
                          </p>
                        </>
                      )}
                  </>
                </div>
              </PCToolTip>
            )}
            <Suspense>
              <Price
                prices={product?.description?.price}
                className={clsx(
                  styles["product-price"],
                  "uppercase",
                  "truncate",
                )}
              />
            </Suspense>
          </div>
          <div className={styles.card__tags}>
            {product?.core?.isNew && (
              <div className={styles.card__tag}>
                <Subtitle noBrackets className={styles["black-tag"]}>
                  NEW IN
                </Subtitle>
              </div>
            )}
            {product?.description?.price?.[0]?.productOnSale && !isSoldOut && (
              <ProductSaleTag
                displayPercentOrSale={product?.displayPercentOrSale}
                discountPercent={discountPercent}
              />
            )}
            {product?.comingSoon &&
              !product?.description?.price?.[0].productOnSale && (
                <div className={clsx(styles.card__tag, styles["black-tag"])}>
                  <Subtitle noBrackets>COMING SOON</Subtitle>
                </div>
              )}
            {(product?.preorder == "True" || product?.preorder == "1") && (
              <div className={clsx(styles.card__tag, styles["grey-tag"])}>
                <Subtitle noBrackets>PRE-ORDER</Subtitle>
              </div>
            )}
            {(product?.onlineExclusive == "True" ||
              product?.onlineExclusive == "1") && (
              <div className={clsx(styles.card__tag, styles["grey-tag"])}>
                <Subtitle noBrackets>ONLINE EXCLUSIVE</Subtitle>
              </div>
            )}

            {isSoldOut &&
              !product?.comingSoon &&
              product?.preorder == "False" && (
                <div className={clsx(styles.card__tag, styles["grey-tag"])}>
                  <Subtitle noBrackets>OUT OF STOCK</Subtitle>
                </div>
              )}
            <ProductLabelTags labels={product?.labels} />
          </div>
        </Link>
      </div>
    </DepictWrapper>
  );
};

export default ProductCard;
